import {
  areSomeNullOrUndefined,
  isNotNullOrUndefined,
  isNullOrUndefined,
  parseJsonSafely,
  withDefault,
} from '@vy/utils';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageParam } from './search-params/use-page-param';
import { usePageSizeParam } from './search-params/use-page-size-param';
import { useEffectOnce } from './use-effect-once';
import { getLocalStorageString, setLocalStorageItem } from '../utils/local-storage';

type StoredPagination = { page?: number; pageSize?: number };

const PAGINATION_STORAGE_PREFIX = 'vy_pagination_';

function getPaginationStorageKey(pathname: string): string {
  return `${PAGINATION_STORAGE_PREFIX}${pathname}`;
}

function getStoredPagination(pathname: string): StoredPagination | null {
  const key = getPaginationStorageKey(pathname);
  const stored = getLocalStorageString(key);

  if (isNullOrUndefined(stored)) {
    return null;
  }

  return parseJsonSafely<StoredPagination>(stored) ?? null;
}

function storePagination(pathname: string, page: number, pageSize: number): void {
  const key = getPaginationStorageKey(pathname);
  setLocalStorageItem(key, JSON.stringify({ page, pageSize }));
}

export type UseTablePaginationOptions = {
  useGlobalState?: boolean;
  defaultPageSize?: number;
};

export type UseTablePaginationReturn = {
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

export const useTablePagination = (options?: UseTablePaginationOptions): UseTablePaginationReturn => {
  const { useGlobalState = true, defaultPageSize = 50 } = options ?? {};
  const { pathname } = useLocation();

  const [globalPage, setGlobalPageParam] = usePageParam();
  const [globalPageSize, setGlobalPageSizeParam] = usePageSizeParam(defaultPageSize);
  const [localPage, setLocalPage] = useState<number>(0);
  const [localPageSize, setLocalPageSize] = useState<number>(defaultPageSize);

  const page = useGlobalState ? withDefault(globalPage, 0) : localPage;
  const pageSize = useGlobalState ? withDefault(globalPageSize, defaultPageSize) : localPageSize;

  const setPage = useGlobalState ? setGlobalPageParam : setLocalPage;
  const setPageSize = useGlobalState ? setGlobalPageSizeParam : setLocalPageSize;

  useEffectOnce(() => {
    if (!useGlobalState) {
      return;
    }

    const storedPagination = getStoredPagination(pathname);

    if (isNullOrUndefined(storedPagination)) {
      return;
    }

    const hasUrlPage = isNotNullOrUndefined(globalPage);
    const hasUrlPageSize = isNotNullOrUndefined(globalPageSize);

    if (!hasUrlPage && isNotNullOrUndefined(storedPagination.page)) {
      setGlobalPageParam(storedPagination.page);
    }

    if (!hasUrlPageSize && isNotNullOrUndefined(storedPagination.pageSize)) {
      setGlobalPageSizeParam(storedPagination.pageSize);
    }
  });

  useEffectOnce(() => {
    if (areSomeNullOrUndefined(useGlobalState, defaultPageSize)) {
      return;
    }

    const hasUrlPageSize = isNotNullOrUndefined(globalPageSize);

    if (hasUrlPageSize) {
      return;
    }

    const storedPagination = getStoredPagination(pathname);

    if (storedPagination?.pageSize) {
      return;
    }

    setPageSize(defaultPageSize);
  });

  useEffect(() => {
    if (useGlobalState) {
      storePagination(pathname, page, pageSize);
    }
  }, [useGlobalState, pathname, page, pageSize]);

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  const onPageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      setPage(0);
    },
    [setPageSize, setPage],
  );

  return {
    page,
    pageSize,
    setPage,
    setPageSize,
    onPageChange,
    onPageSizeChange,
  };
};
